// Approximate planar distance from a point to a polyline, in meters.
//
// Used to RANK how closely a route's path passes a stop (for the "sort matched
// routes by proximity" behaviour in the planner). This is a ranking aid, not a
// survey-grade measurement: it projects lat/lng to a local equirectangular plane
// around the point's latitude (accurate to well under a percent across a single
// delivery region) and returns the shortest point-to-segment distance.

const EARTH_RADIUS_M = 6371000;
const DEG = Math.PI / 180;

// Project a lat/lng to local planar meters around a reference latitude.
const toXY = (lat, lng, lat0) => [
  lng * DEG * Math.cos(lat0 * DEG) * EARTH_RADIUS_M,
  lat * DEG * EARTH_RADIUS_M
];

// Squared distance from point (px,py) to segment (ax,ay)-(bx,by).
const segDistSq = (px, py, ax, ay, bx, by) => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / len2;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  const ex = px - cx;
  const ey = py - cy;
  return ex * ex + ey * ey;
};

const isFiniteNum = n => typeof n === "number" && isFinite(n);

// point: { lat, lng }. polyline: array of { lat, lng } (length >= 1).
// Returns the shortest distance in meters, or Infinity for invalid input.
export const distancePointToPolyline = (point, polyline) => {
  if (
    !point ||
    !isFiniteNum(point.lat) ||
    !isFiniteNum(point.lng) ||
    !Array.isArray(polyline)
  ) {
    return Infinity;
  }
  const pts = polyline.filter(
    p => p && isFiniteNum(p.lat) && isFiniteNum(p.lng)
  );
  if (pts.length === 0) return Infinity;

  const lat0 = point.lat;
  const [px, py] = toXY(point.lat, point.lng, lat0);

  if (pts.length === 1) {
    const [ax, ay] = toXY(pts[0].lat, pts[0].lng, lat0);
    const ex = px - ax;
    const ey = py - ay;
    return Math.sqrt(ex * ex + ey * ey);
  }

  let min = Infinity;
  for (let i = 0; i < pts.length - 1; i++) {
    const [ax, ay] = toXY(pts[i].lat, pts[i].lng, lat0);
    const [bx, by] = toXY(pts[i + 1].lat, pts[i + 1].lng, lat0);
    const d = segDistSq(px, py, ax, ay, bx, by);
    if (d < min) min = d;
  }
  return Math.sqrt(min);
};
